if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        let giftConfig = this.getGiftConfig();
        // Collection cards with a direct Add to Cart button do not open a quick-add
        // sheet; load the same gift configuration from this product's page.
        if (!giftConfig && this.dataset.haldenGiftProductUrl) {
          try {
            giftConfig = await this.loadCollectionGiftConfig();
          } catch (error) {
            console.error('HALDEN collection gift:', error);
            this.handleErrorMessage('Could not load the complimentary gift. Please try again.');
            this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.classList.remove('loading');
            this.querySelector('.loading__spinner')?.classList.add('hidden');
            return;
          }
        }
        // The complimentary gift is a cart-wide one-time offer, not one per item.
        // Read the actual cart before building the add request.
        let giftAlreadyInCart = false;
        if (giftConfig) {
          try {
            const cartResponse = await fetch(`${routes.cart_url}.js`, {
              headers: { Accept: 'application/json' },
            });
            if (!cartResponse.ok) throw new Error('Unable to check complimentary gift');
            const currentCart = await cartResponse.json();
            giftAlreadyInCart = currentCart.items.some((item) => item.properties?._halden_gift === 'true');
          } catch (error) {
            // The cart sync also removes duplicates after an add if this check fails.
            console.warn('HALDEN gift check:', error);
          }
        }
        const giftLines = giftConfig && !giftAlreadyInCart
          ? giftConfig.variantIds
              .filter((giftVariantId) => String(giftVariantId) !== String(variantId))
              .slice(0, 1)
              .map((giftVariantId) => ({
                merchandiseId: String(giftVariantId),
                quantity: 1,
              }))
          : [];

        if (giftLines.length) {
          const mainItem = this.buildMainCartItem(formData, variantId, quantity);
          const giftItems = giftLines.map((line) => ({
            id: line.merchandiseId,
            quantity: line.quantity,
            properties: {
              _halden_gift: 'true',
              _halden_gift_for: giftConfig.parentProductId,
              _halden_gift_mode: 'one',
              _halden_gift_label: giftConfig.giftLabel,
            },
          }));

          const payload = { items: [mainItem, ...giftItems] };
          if (this.cart) {
            payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
            payload.sections_url = window.location.pathname;
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = JSON.stringify(payload);
        } else {
          delete config.headers['Content-Type'];
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity, giftLines);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      getGiftConfig() {
        // Never pick a gift belonging to an unrelated product elsewhere on the page.
        const scope = this.closest('impact-quick-add-sheet') || this.closest('product-info') ||
          this.closest('.product') || this.closest('.card-wrapper') || this;
        const giftBlock = scope.querySelector('[data-halden-gift-products]');
        return this.giftConfigFromBlock(giftBlock);
      }

      giftConfigFromBlock(giftBlock) {
        if (!giftBlock) return null;
        const variantIds = [...giftBlock.querySelectorAll('[data-gift-variant-id]')]
          .map((item) => item.dataset.giftVariantId)
          .filter(Boolean)
          .filter((id, index, ids) => ids.indexOf(id) === index);
        if (!variantIds.length || !giftBlock.dataset.parentProductId) return null;
        return {
          variantIds,
          parentProductId: giftBlock.dataset.parentProductId,
          quantityMode: 'one',
          giftLabel: giftBlock.dataset.giftLabel || 'Gift',
        };
      }

      async loadCollectionGiftConfig() {
        const response = await fetch(this.dataset.haldenGiftProductUrl, {
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Unable to load product gift settings');
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const giftBlocks = html.querySelectorAll('[data-halden-gift-products]');
        const productId = this.dataset.haldenGiftProductId;
        const block = [...giftBlocks].find((item) => item.dataset.parentProductId === productId);
        return this.giftConfigFromBlock(block);
      }

      buildMainCartItem(formData, variantId, quantity) {
        const item = { id: variantId, quantity };
        const properties = {};

        for (const [key, value] of formData.entries()) {
          const propertyMatch = key.match(/^properties\[(.+)\]$/);
          if (propertyMatch && value !== '') properties[propertyMatch[1]] = value;
        }

        if (Object.keys(properties).length) item.properties = properties;

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) item.selling_plan = sellingPlan;

        return item;
      }

      createCartLinesUpdateEvent(variantId, quantity, giftLines = []) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }, ...giftLines],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
